export type CompImagePriority = "Primary" | "Secondary";
export type CompImageEvidenceRole = "Proof" | "Context" | "Decorative Support";

export interface CompImageAsset {
  id: string;
  sourceProperty: string;
  sourceType: "Street View" | "Static Map";
  imagePath: string;
  pageTarget: string;
  componentTarget: string;
  priority: CompImagePriority;
  usageIntent: string;
  caption: string;
  altText: string;
  notes: string;
  tier: "Primary" | "Secondary";
  evidenceRole: CompImageEvidenceRole;
}

export const COMP_IMAGE_ASSETS: CompImageAsset[] = [
  {
    id: "104-f-st-street-view",
    sourceProperty: "104 F St, Lynchburg, VA 24504",
    sourceType: "Street View",
    imagePath: "/comps/104-f-st-streetview.jpg",
    pageTarget: "/comps",
    componentTarget: "Micro-area proof panel",
    priority: "Primary",
    usageIntent: "Lead evidence image for the hyper-local compact-product precedent story.",
    caption: "104 F St shows a 522 sf, 1 bed compact new-construction product that has already found market support nearby.",
    altText: "Street-view image of 104 F Street in Lynchburg, Virginia, used as hyper-local compact-home precedent for 400 Norwood.",
    notes: "Use as evidence, not hero lifestyle imagery. Best paired with 110 F St to show repeatability rather than anecdote.",
    tier: "Primary",
    evidenceRole: "Proof"
  },
  {
    id: "110-f-st-street-view",
    sourceProperty: "110 F St, Lynchburg, VA 24504",
    sourceType: "Street View",
    imagePath: "/comps/110-f-st-streetview.jpg",
    pageTarget: "/comps",
    componentTarget: "Micro-area proof panel",
    priority: "Primary",
    usageIntent: "Second proof image showing that compact infill product was not a one-off in the immediate micro-area.",
    caption: "110 F St reinforces the repeatability of the compact-product pattern by showing another nearby 522 sf, 1 bed trade.",
    altText: "Street-view image of 110 F Street in Lynchburg, Virginia, used to reinforce nearby compact-home precedent for 400 Norwood.",
    notes: "Use beside 104 F St in a paired evidence layout or precedent strip.",
    tier: "Primary",
    evidenceRole: "Proof"
  },
  {
    id: "104-f-st-map",
    sourceProperty: "104 F St, Lynchburg, VA 24504",
    sourceType: "Static Map",
    imagePath: "/comps/104-f-st-map.jpg",
    pageTarget: "/comps",
    componentTarget: "Location context inset",
    priority: "Secondary",
    usageIntent: "Shows how close the comp sits to the subject's downtown-adjacent urban fabric.",
    caption: "Map view for 104 F St supports the micro-location credibility of the precedent story.",
    altText: "Static map locating 104 F Street in Lynchburg, Virginia, near the 400 Norwood subject area.",
    notes: "Context support only. Stronger as a location inset than as a headline visual.",
    tier: "Secondary",
    evidenceRole: "Context"
  },
  {
    id: "110-f-st-map",
    sourceProperty: "110 F St, Lynchburg, VA 24504",
    sourceType: "Static Map",
    imagePath: "/comps/110-f-st-map.jpg",
    pageTarget: "/comps",
    componentTarget: "Location context inset",
    priority: "Secondary",
    usageIntent: "Confirms the nearby clustering of the F St compact-product precedent set.",
    caption: "Map view for 110 F St helps show that the precedent story is hyper-local rather than citywide abstraction.",
    altText: "Static map locating 110 F Street in Lynchburg, Virginia, near the 400 Norwood subject area.",
    notes: "Secondary support asset. Best used after the street-view proof images.",
    tier: "Secondary",
    evidenceRole: "Context"
  }
];

export const COMP_IMAGE_LIMITATIONS = [
  "100 F St is part of the precedent story, but no clean image payload was available in the current connector pass.",
  "Street-view imagery supports product realism and micro-area grounding, not STR revenue performance proof.",
  "These assets should stay in evidence surfaces such as precedent panels, comp cards, or map insets rather than the homepage hero."
];

export const COMP_IMAGE_INTEGRATION_RECOMMENDATIONS = [
  "Use 104 F St and 110 F St street views first on the Comps page as the primary micro-area proof block.",
  "Use the two static maps as secondary context beneath the proof images or in a smaller inset row.",
  "Do not lead with these assets on the homepage or present them as direct STR performance proof."
];
